import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import type { ExamsApi, Topic } from '../api/exams'
import type { QuestionsApi, TopicCount } from '../api/questions'
import { TopicsGrid, type Filing, type TopicRow } from './TopicsGrid'

/**
 * Questions > Coverage: how the Question Bank sits under the Exam's Topics.
 *
 * WHY THERE IS NO FOREIGN KEY. A Question carries a Topic Code as text and is never joined
 * to a Topic, and a Topic dropped from the Exam must not take the Questions written for it
 * with it. So the check is here, in the report: a code the Exam does not have is a row marked
 * "Not a Topic", not an upload the API turned away (app:R83).
 *
 * Read-only. The Topics are edited under the Exam menu, where they belong to the Exam; this
 * screen is the Questions area's view of them, because writing Questions is what it is for.
 */
export function Coverage({
  questions,
  exams,
  exam,
}: {
  questions: QuestionsApi
  exams: ExamsApi
  exam: string
}) {
  // Its own key rather than a slice of ['questions']: a different shape from a different
  // endpoint. Uploading invalidates both, because new files move these counts.
  const counts = useQuery({ queryKey: ['question-topics'], queryFn: () => questions.topics() })
  const topics = useQuery({ queryKey: ['topics', exam], queryFn: () => exams.topics(exam) })

  const summary = useMemo(
    () => summarise(counts.data ?? [], exam, topics.data?.topics ?? []),
    [counts.data, exam, topics.data],
  )

  const failure = counts.error?.message ?? topics.error?.message ?? null

  return (
    <>
      <h2 className="font-heading text-h2 font-bold text-heading">Coverage</h2>

      {failure && (
        <p role="alert" className="mt-4 border-l-2 border-red-700 pl-3 text-red-700">
          {failure}
        </p>
      )}

      {topics.data?.topics.length === 0 && (
        <p className="mt-4 text-muted">
          No Topics for {exam} yet. Until they are entered under the Exam menu, every Topic Code the
          Bank cites shows as not a Topic, which is exactly what it is.
        </p>
      )}

      <TopicsGrid rows={summary.rows} total={summary.total} pending={counts.isPending || topics.isPending} />
    </>
  )
}

/**
 * One Exam's rows: every Topic the Exam lists and every Topic Code the Bank cites,
 * with the shares worked out, and the totals row the grid pins to its foot.
 *
 * The two lists are merged rather than one filtered by the other, because both directions
 * are findings. A Topic with no Questions is work not started; a Topic Code the Exam does
 * not have is a Question filed against something that is not in the specification -- a
 * typo, or a Topic dropped since it was written.
 *
 * The Exam's order first, because that is the order the specification reads in, then the
 * strays, then the unfiled row.
 */
export function summarise(
  counts: TopicCount[],
  exam: string,
  topics: Pick<Topic, 'code' | 'name'>[],
): { rows: TopicRow[]; total: TopicRow } {
  const mine = counts.filter((row) => row.exam === exam)
  const filed = new Set(topics.map((topic) => topic.code.toUpperCase()))
  const counted = new Map(
    mine.filter((row) => row.topicCode).map((row) => [row.topicCode!.toUpperCase(), row]),
  )

  // The shares are of everything, so they add up to 100% and the unfiled row is visibly
  // part of the Bank.
  const generated = mine.reduce((sum, row) => sum + row.generated, 0)

  const row = (
    topicCode: string | null,
    topic: string | null,
    filing: Filing,
    count: TopicCount | undefined,
  ): TopicRow => ({
    topicCode,
    topic,
    filing,
    generated: count?.generated ?? 0,
    generatedShare: share(count?.generated ?? 0, generated),
  })

  const listed = topics.map((topic) =>
    row(topic.code, topic.name, 'A Topic', counted.get(topic.code.toUpperCase())),
  )

  const strays = mine
    .filter((count) => count.topicCode !== null && !filed.has(count.topicCode.toUpperCase()))
    .map((count) => row(count.topicCode, count.topic, 'Not a Topic', count))

  const unfiled = mine
    .filter((count) => count.topicCode === null)
    .map((count) => row(null, null, 'Unfiled', count))

  const rows = [...listed, ...strays, ...unfiled]

  return {
    rows,
    total: {
      topicCode: 'Total',
      topic: `${rows.length} ${rows.length === 1 ? 'row' : 'rows'}`,
      filing: 'A Topic',
      generated,
      generatedShare: generated === 0 ? null : 100,
    },
  }
}

function share(of: number, outOf: number): number | null {
  return outOf === 0 ? null : (of / outOf) * 100
}
